/**
 * Datenzugriff der Sektion „Anfos & Umsatz" (/auswertungen). Backend: node-app/src/routes/auswertungen.ts,
 * Gruppe fit_products. Antworten im Hausformat `{ success, data }`.
 */
import { fetchData } from './apiUtils';
import type {
  RoomRetentionData,
  WebinarComparisonData,
  WebinarEngagementDetailData,
  WebinarGroupedEvaluationDetailData,
  WebinarMappingBreakdownData,
  WebinarOrderDetailData,
  WebinarRegistrationData,
} from './webinarStatisticsData';

export interface SourceFreshness {
  key: string;
  label: string;
  /** Berliner Zeit `YYYY-MM-DD HH:mm:ss` oder null. */
  updated_at: string | null;
  note: string;
}

export interface AuswertungenMeta {
  as_of: string;
  sources: SourceFreshness[];
  /**
   * `provisional`: davon vorläufig aus PX-Data ergänzt (Zahlstand unbekannt). Paket F (optional, ältere API ohne):
   * `ohne_kennung` = davon über die Anmeldung (ohne Kennung) beim Webinar, `ausgeschlossen` = je Grund nicht gezählt.
   */
  population: {
    anfos: number;
    provisional: number;
    since: string | null;
    ohne_kennung?: number;
    ausgeschlossen?: { mitarbeiter: number; vorlaeufig_alt: number };
    regeln?: { fenster_stunden: number; vorlaeufig_max_tage: number; mitarbeiter_domains: string[] };
  };
}

export interface ChannelDefinition {
  key: string;
  /** Name in der Sektion (seit dem P3-Review der Sektionsname, z. B. „Newsletter-Redlink zum Produkt"). */
  label: string;
  /** Name derselben Kategorie auf /plx-orders („Quelle") und in der Welcome-Auswertung. */
  quelle_label?: string;
  description: string;
  rules: string[];
}

export interface AuswertungenDefinitionen {
  channels: ChannelDefinition[];
  constants: { session_hours: number; mature_days: number; duplicate_days: number };
}

async function unwrap<T>(url: string): Promise<T> {
  const res = await fetchData<T>(url);
  if (!res.success || res.data === undefined) throw new Error(res.error || 'Laden fehlgeschlagen');
  return res.data;
}

export const fetchAuswertungenMeta = () => unwrap<AuswertungenMeta>('/api/auswertungen/meta');
export const fetchAuswertungenDefinitionen = () =>
  unwrap<AuswertungenDefinitionen>('/api/auswertungen/definitionen');

/** `2026-10-05 22:19:03` → `05.10.2026, 22:19` — ohne Date-Objekt, also ohne Zeitzonen-Umrechnung. */
export function formatBerlinWallclock(value: string | null): string {
  if (!value) return '–';
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(value);
  if (!m) return value;
  return m[4] ? `${m[3]}.${m[2]}.${m[1]}, ${m[4]}:${m[5]}` : `${m[3]}.${m[2]}.${m[1]}`;
}

// ─── Kapitel „Webinare & Produkte" ──────────────────────────────────────────────────────────
// Backend: node-app/src/services/auswertungen/webinareProdukteService.ts. Euro-Beträge kommen auf
// Cent gerundet; Quoten und Raten UNGERUNDET (Prozent, nicht Bruch) — gerundet wird einmal, beim Anzeigen.

export interface Fenster {
  asOf: string;
  von: string;
  bis: string;
}

// Stufe 0 des P1/P2-Umbaus (Plan 2026-10-23-p1-p2-kpi-review-umbau): Felder, die das Backend erst in Stufe 1
// liefert, sind OPTIONAL — die Karten müssen ihr Fehlen aushalten (Deploy-Reihenfolge, Cache). Geteilte Formen:

/** Etikett „jung" je Zeile (P2-V3 § 2.0/3): Anteil der Anfos (ohne vorläufige) jünger als `jung_tage`. */
export interface JungFelder {
  is_young?: boolean;
  /** Anteil 0–1. */
  young_share?: number | null;
}

/** Schwellen der Grau-Regel „jung" aus der API (wie `reife_tage` bei den P3-Blogs). */
export interface JungSchwellen {
  jung_tage?: number;
  /** Bruch, z. B. 0,2. */
  jung_anteil?: number;
  reife_tage?: number;
}

/** Zeitraum gegen Zeitraum, Zwei-Anteile-z-Test + Holm über die Familie der Karte (P2-V3 § 2.0 Regel 8). */
export interface ZeitraumTest {
  z: number | null;
  p: number;
  p_holm: number | null;
  /** ▲/▼ nur bei Holm ≤ 0,05; Erklärung `TERMS.abweichungZeitraumMarke`. */
  flag: 'up' | 'down' | null;
}

/** Marke „geschätzt" (Till 23.10.): Anfos der Zeile auf Angeboten über dem m5-Preisdeckel (`wertGeschaetzt.ts`). */
export interface WertGeschaetztFelder {
  wert_geschaetzt_anfos?: number;
}

export interface ProduktZeile {
  plx_product: string;
  name: string;
  anfos: number;
  /** Vorläufig aus PX-Data ergänzt (Zahlstand unbekannt) — in `anfos` enthalten, in keiner Quote. */
  provisional: number;
  webinar_anfos: number;
  upsell_anfos: number;
  paid_revenue: number;
  /** Bezahlt ÷ Anfos mit bekanntem Zahlstand (ohne vorläufig ergänzte). */
  paid_per_anfo: number | null;
  mature_anfos: number;
  mature_paid: number;
  pay_rate: number | null;
  avg_list_price: number | null;
  list_price_estimated_share: number | null;
  anfo_wert: number | null;
  webinars_window: number;
  last_webinar: string | null;
  next_webinar: string | null;
  rest_days: number | null;
  // P1-V3 § 2.4 (über `kennzahlenSql`/`kennzahlenAus`/`markiereBezahlquote` wie die P3-Kachel):
  /** Erwarteter Wert (Summe), ohne vorläufige. */
  value_sum?: number;
  with_value?: number;
  /** Ø Erwarteter Wert je Anfo mit Wert. */
  value_per_anfo?: number | null;
  /** „Über Webinare": Zähler und Nenner ohne vorläufige (Regel 13). */
  webinar_anfos_fest?: number;
  anfos_fest?: number;
  pay_rate_p?: number | null;
  pay_rate_p_holm?: number | null;
  pay_rate_flag?: 'up' | 'down' | null;
  payers?: number;
  avg_age_days?: number | null;
  veraenderung_vorjahr?: Veraenderung;
  /** Erste Vorjahres-Anfo (YYYY-MM-DD), wenn sie mehr als 1 Monat nach Fensterbeginn liegt — Etikett „im Vorjahr erst ab". */
  vorjahr_erst_ab?: string | null;
  /** Einmalkauf (Konstante neben der Einmalfaktura-Liste) bzw. „kein Webinar mehr" (Konstante `AUSGELAUFEN`). */
  ist_einmalkauf?: boolean;
  nachfolger?: string | null;
  bestandskunden?: number | null;
  /** Typischer Erwarteter Wert je Termin des Produkts (Median, Wahl). */
  wert_je_termin_typisch?: number | null;
  wert_geschaetzt_anfos?: number;
  /** Anteil Erstbesteller (ohne vorläufige), Prozent. */
  first_buyer_share?: number | null;
  /** Workshop E3: Ø Listenpreis netto — nur außerhalb von 12 Monaten gesetzt (`avg_list_price` ist brutto). */
  avg_list_price_net?: number | null;
}

export interface ProdukteAntwort {
  window: Fenster;
  rows: ProduktZeile[];
  /** P1-V3 § 2.4: Vorjahr (bei 12 Monaten = „gleich lang davor"), Schnitt am Datenstand. */
  vergleich?: VergleichsFenster;
  grau_schwellen?: GrauSchwellen;
  /** Werbeaktionen mit geschätztem Wert (heute TAP0004), für Etikett und Erklärung. */
  wert_geschaetzt_angebote?: string[];
  summary: Omit<
    ProduktZeile,
    'plx_product' | 'name' | 'anfo_wert' | 'last_webinar' | 'next_webinar' | 'rest_days'
  >;
  /** Workshop E3: nur außerhalb von 12 Monaten gesetzt (dann kein typischer Wert je Termin, keine Veränderung). */
  zeitraum?: { key: Exclude<ProdukteZeitraum, '12m'>; historie_ab: string };
}

/** Workshop E3 (29.10.2026): Zeitraum der Karte „Produkte". */
export type ProdukteZeitraum = '12m' | '2025' | 'gesamt';

export interface EffizienzTeil {
  sends: number;
  contacts: number;
  anfos: number;
  paid_revenue: number;
  /** Summe der Werte bei Bestellung (je Anfo Angebot, sonst Produkt). */
  m4_value: number;
  m4_contacts: number;
  anfos_per_1k: number | null;
  paid_per_1k: number | null;
  /** Erwarteter Wert (bei Bestellung) je 1.000 Kontakte. */
  m4_per_1k: number | null;
  /** P1-V3 § 2.5: Abmeldungen und je 1.000 Kontakte (`TERMS.abmeldungenJe1kVersand`). */
  unsubscribes?: number;
  unsubscribes_per_1k?: number | null;
}

export interface EffizienzZeile {
  plx_product: string;
  name: string;
  anfo_wert: number | null;
  web: EffizienzTeil;
  plx: EffizienzTeil;
  contacts: number;
  anfos: number;
  /** E3: „bis x € mit allen Webinar-Käufen" — Σ `wert_summe_fest` der zählenden Termine ÷ dieselben Kontakte × 1.000. */
  obergrenze_per_1k?: number | null;
  obergrenze_anfos?: number;
  /** Alle Anfos des Artikels vorläufig → „—" (Regel 13). */
  nur_vorlaeufig?: boolean;
}

export interface EffizienzAntwort {
  window: { von: string; bis: string; asOf: string; reife_tage: number };
  anfo_wert_stand: string;
  /** Gesetzt bei Stichtag in der Vergangenheit: Erst-Kontakt-Anfos sind Stand heute. */
  stichtag_hinweis: string | null;
  rows: EffizienzZeile[];
  summary: { web: EffizienzTeil; plx: EffizienzTeil; contacts: number; anfos: number };
  plx_zuordnung: { anfos: number; zugeordnet: number; ohne_versand: number };
  /** Nicht gezählte Direkt-Mailing-Versände mit Link auf eine Webinar-Anmeldung (Webinar-Einladungen). */
  plx_anmelde_versaende: { sends: number; contacts: number };
  webinare_im_fenster: number;
  /** Artikel, die die Summenzeile Direkt-Mailing auslässt (alle Anfos vorläufig, z. B. DKD Einmalfaktura). */
  ohne_vorlaeufige_artikel?: { plx_product: string; name: string; anfos: number }[];
}

export type KandidatGrund =
  | 'keine_kontakte'
  | 'jung'
  | 'vor_kontaktbeginn'
  | 'zu_wenige_kontakte'
  | 'kein_anfo_wert'
  | 'produkt_zu_frisch'
  | 'kein_produkt';

export interface WebinarZeile {
  webinar_id: string;
  title: string;
  /** Öffentlicher Titel (Anzeigename, sonst Arbeitstitel). */
  public_title: string;
  plx_product: string | null;
  product_name: string | null;
  group_key: string;
  type: string | null;
  is_recurring: boolean;
  start_date: string;
  age_days: number;
  is_young: boolean;
  /** Personen mit Live-Anmeldung (rechtzeitig oder Späteinlass) bis zum Stichtag. */
  registrations: number;
  /** Personen, die sich nur nach Sendestart angemeldet haben — nicht in `registrations`. */
  registrations_after_start: number;
  anfos: number;
  /** Vorläufig aus PX-Data ergänzte Anfos (Zahlstand unbekannt), nicht in der Bezahlquote. */
  provisional: number;
  /** Davon über die Anmeldung (ohne Kennung) zugeordnet (Paket F). */
  anfos_ohne_kennung?: number;
  paid_revenue: number;
  mature_anfos: number;
  mature_paid: number;
  pay_rate: number | null;
  anfos_per_100_regs: number | null;
  sends: number;
  contacts: number;
  send_anfos: number;
  send_paid_revenue: number;
  first_send: string | null;
  last_send: string | null;
  anfos_per_1k: number | null;
  /** Bisher bezahlt (netto) der Erst-Kontakt-Anfos je 1.000 Kontakte — echtes Geld neben dem Wert. */
  paid_per_1k: number | null;
  anfo_wert: number | null;
  /** Mittlerer Wert bei Bestellung der Anfos (Angebot, sonst Produkt). */
  wert_je_anfo: number | null;
  wert_quelle: 'bestellt' | 'beworben' | null;
  counted: boolean;
  value_per_1k: number | null;
  product_mean_per_1k: number | null;
  prior_mean_per_1k: number | null;
  prior: 'produkt' | 'gesamt' | null;
  own_weight: number | null;
  value_shrunk_per_1k: number | null;
  product_rest_days: number | null;
  candidate_rank: number | null;
  candidate_reasons: KandidatGrund[];
  // P1-V3 § 2.3 / K6:
  /** Erwarteter Wert (Summe) ohne vorläufige — Tabelle, Detail-Kachel, Seitenkopf. `wert_summe` bleibt für die Prognose. */
  wert_summe_fest?: number;
  with_value_fest?: number;
  /** „Zahlen noch offen bis TT.MM." = Termin + 14 Tage (YYYY-MM-DD). */
  zahlen_offen_bis?: string | null;
  unsubscribes?: number;
  unsubscribes_per_1k?: number | null;
  payers?: number;
  avg_age_days?: number | null;
  wert_geschaetzt_anfos?: number;
  /** Workshop A2: Σ CPA bei Bestellung (netto) ohne vorläufige und wie viele Anfos einen CPA haben. */
  cpa_summe_fest?: number;
  with_cpa_fest?: number;
}

/** Teilnahme je Termin (P1-V3 § 3 „Show-up je Termin", eigener Endpunkt, fail-soft). */
export interface ShowUpZeile {
  webinar_id: string;
  /** Vor Beginn angemeldet (`post_start = 0`) — Nenner des Show-up. */
  timely: number | null;
  /** Teilnehmer unter den vor Beginn Angemeldeten — Zähler des Show-up (K1). */
  attended_timely: number | null;
  /** Alle Teilnehmer inkl. Eingestiegene. */
  attendees: number | null;
  /** Während des Webinars eingestiegen (`post_start = 3`). */
  joined_live: number | null;
  /** Zu spät für live (`post_start IN (1,2)`); vor 25.08.2026 null. */
  too_late: number | null;
  show_up: number | null;
  /** Grund für „—": Messlücke (Liste), Tracking-Warnung, übernommene Anmeldungen. */
  grund: 'messluecke' | 'tracking_warnung' | 'migriert' | null;
}
export interface ShowUpAntwort {
  window: Fenster;
  rows: ShowUpZeile[];
}

/** Vergleichszeile „Typischer Termin" (Median der fertigen Termine je Spalte, P1-V3 § 2.3). */
export interface TypischerTermin {
  termine: number;
  wert_summe_fest: number | null;
  anfos: number | null;
  contacts: number | null;
  registrations: number | null;
  show_up: number | null;
  anfos_per_100_regs: number | null;
  value_per_1k: number | null;
}

/** Seitenkopf P1 (E1): drei Kacheln. */
export interface P1Seitenkopf {
  wert_summe_fest: number;
  termine: number;
  anfos: number;
  /** Anteil Kanal Webinar am Erwarteten Wert aller Anfos (Zähler, Nenner in €). */
  kanal_webinar: { wert: number; gesamt: number } | null;
  letzte_acht: { median: number | null; p25: number | null; p75: number | null; von: string; bis: string; uebrige_median: number | null; uebrige: number; flag: 'up' | 'down' | null } | null;
}

export interface GruppenZeile {
  group_key: string;
  title: string;
  plx_product: string | null;
  product_name: string | null;
  runs: { webinar_id: string; start_date: string; value_per_1k: number | null; counted: boolean }[];
  last_run: string;
  last_webinar_id: string;
  last_counted_value_per_1k: number | null;
  last_counted_start: string | null;
  registrations: number;
  /** Personen nur mit Anmeldung nach Sendestart, über alle Läufe. */
  registrations_after_start: number;
  anfos: number;
  contacts: number;
  send_anfos: number;
  anfos_per_100_regs: number | null;
  wert_je_anfo: number | null;
  /** Bisher bezahlt (netto) je 1.000 Kontakte der zählenden Läufe. */
  paid_per_1k: number | null;
  value_per_1k: number | null;
  product_mean_per_1k: number | null;
  prior_mean_per_1k: number | null;
  prior: 'produkt' | 'gesamt' | null;
  own_weight: number | null;
  value_shrunk_per_1k: number | null;
  product_rest_days: number | null;
  candidate_rank: number | null;
  candidate_reasons: KandidatGrund[];
}

export interface RankingAntwort {
  window: Fenster;
  versand: { von: string; bis: string };
  anfo_wert_stand: string;
  /** Gesetzt bei Stichtag in der Vergangenheit: Erst-Kontakt-Anfos sind Stand heute. */
  stichtag_hinweis: string | null;
  regel: { minContacts: number; minRestDays: number; cv2: number; cv2_gruppen: number; junges_webinar_tage: number };
  product_means: Record<string, number>;
  rows: WebinarZeile[];
  groups: GruppenZeile[];
  /** Empfehlung „nächstes Webinar" (Backend `helpers/webinarPrognose.ts`); null = Rechnung gescheitert. */
  prognose: PrognoseErgebnis | null;
  /** Kopf der Karte: festgeschrieben (Montags-Cron) oder Vorschau. */
  empfehlung: EmpfehlungKopf | null;
  sperren: VorschlagSperre[];
  anlaesse: Anlass[];
  zusatz_fehler: string[];
  /** E5: Prognose für den nächsten FREIEN Sonntag, nur Oberfläche; null = Zielsonntag ist frei. */
  prognose_frei?: PrognoseErgebnis | null;
  typisch?: TypischerTermin | null;
  seitenkopf?: P1Seitenkopf | null;
}

// ─── Empfehlung „nächstes Webinar" (Plan 2026-10-14) ─────────────────────────────────────────

export type PrognoseGrund = 'kein_produkt' | 'produkt_zu_frisch' | 'konzept_zu_frisch' | 'thema_zu_frisch' | 'nicht_vorschlagen' | 'produkt_gesperrt';
export type EmpfehlungKlasse = 'top1' | 'top2' | 'top3' | 'test' | 'kandidat' | 'gesperrt';

export interface Anlass {
  id: number;
  datum: string;
  titel: string;
  thema: string | null;
  notiz?: string | null;
  erstellt_von?: string | null;
}

export interface PrognoseKandidat {
  key: string;
  plx_product: string | null;
  title: string;
  last_webinar_id: string;
  last_run: string;
  runs: {
    webinar_id: string;
    start_date: string;
    concept: string | null;
    title: string;
    wert: number | null;
    reif: boolean;
    /** P1-V3 § 2.2: Kontakte des Laufs („bei 202 Tsd. Kontakten") und `wert_summe_fest`. */
    kontakte?: number | null;
    wert_fest?: number | null;
  }[];
  reife_laeufe: number;
  thema: string | null;
  thema_quelle: 'ki' | 'manuell' | null;
  pause_produkt: number | null;
  pause_konzept: number | null;
  pause_thema: number | null;
  pausenfaktor: number;
  median: number | null;
  p25: number | null;
  p75: number | null;
  rang: number | null;
  klasse: EmpfehlungKlasse;
  gleichwertig: boolean;
  gruende: PrognoseGrund[];
  frei_ab: string | null;
  anlass: Anlass | null;
  bestandskunden: number | null;
  bezahlquote: number | null;
  warum: string[];
}

export interface NeuesKonzept {
  plx_product: string;
  reife_laeufe: number;
  pause_produkt: number | null;
  median: number | null;
  p25: number | null;
  p75: number | null;
  gruende: PrognoseGrund[];
  /** P1-V3 § 2.2: „frei ab" auch für ganz neue Webinare. */
  frei_ab?: string | null;
}

export interface PrognoseErgebnis {
  stichtag: string;
  zieltermin: string;
  modell: {
    k_gruppe: number | null;
    k_produkt: number;
    sigma: number;
    niveau_eur: number | null;
    reife_laeufe: number;
    /** P1-V3 § 2.2 Fußnote, live statt fest: Pausenstufen (Tage → Faktor). */
    pausen_stufen?: { tage: number; faktor: number }[];
  };
  kandidaten: PrognoseKandidat[];
  neue_konzepte: NeuesKonzept[];
  geplant: { webinar_id: string; title: string; plx_product: string | null; key: string | null; rang: number | null; gruende: PrognoseGrund[] }[];
}

export interface SnapshotKandidat {
  key: string;
  plx_product: string | null;
  title: string;
  klasse: EmpfehlungKlasse;
  rang: number | null;
  median: number | null;
  p25: number | null;
  p75: number | null;
  gleichwertig: boolean;
  gruende: PrognoseGrund[];
  frei_ab: string | null;
  anlass: { datum: string; titel: string } | null;
  warum: string[];
  runs: { webinar_id: string; concept: string | null; title: string }[];
}

export interface EmpfehlungKopf {
  quelle: 'festgeschrieben' | 'vorschau';
  ziel_datum: string;
  erstellt_am: string | null;
  kandidaten: SnapshotKandidat[];
  geplant: PrognoseErgebnis['geplant'];
}

export interface VorschlagSperre {
  id: number;
  art: 'produkt' | 'gruppe';
  schluessel: string;
  grund: string | null;
  erstellt_von: string | null;
  erstellt_am: string;
}

export type AbgleichZustand = 'befolgt' | 'test' | 'kandidat_nicht_empfohlen' | 'gesperrt_gelaufen' | 'nicht_prognostiziert';

export interface NachverfolgungZeile {
  ziel_datum: string;
  erstellt_am: string;
  empfohlen: SnapshotKandidat[];
  stand: 'offen' | 'kein_webinar' | 'gelaufen';
  abgleich: {
    webinar_id: string;
    title: string;
    plx_product: string | null;
    zustand: AbgleichZustand;
    rang: number | null;
    wert: number | null;
    reif: boolean;
    p25: number | null;
    p75: number | null;
    in_spanne: boolean | null;
  }[];
  abweichung_grund: string | null;
  abweichung_notiz: string | null;
  abweichung_von: string | null;
  /** K7: Wertbasis des Schnappschusses (`modell` enthält `wert=bestellung`); nur diese zählen in der Bilanz. */
  wertbasis?: 'bestellung' | 'alt';
  /** K8: Sonntag war bei der Empfehlung schon belegt (`geplant` nicht leer) — zählt bei „gelaufen" nicht. */
  geplant_bei_empfehlung?: boolean;
}

export interface NachverfolgungAntwort {
  zeilen: NachverfolgungZeile[];
  bilanz: {
    in_spanne: number;
    beurteilbar: number;
    /** P1-V3 § 2.2: „Empfehlung gelaufen: n von m freien Sonntagen". */
    gelaufen?: number;
    freie_sonntage?: number;
    ab?: string;
  };
}

/** Feste Themenliste — dieselben Schlüssel wie `THEMEN` im Backend (`helpers/webinarPrognose.ts`). */
export const THEMEN: { key: string; label: string }[] = [
  { key: 'chips_ki', label: 'Chips & KI' },
  { key: 'tech', label: 'Tech allgemein' },
  { key: 'energie', label: 'Energie' },
  { key: 'ruestung', label: 'Rüstung' },
  { key: 'rohstoffe', label: 'Rohstoffe' },
  { key: 'raumfahrt', label: 'Raumfahrt' },
  { key: 'trading_cash', label: 'Trading & Cash' },
  { key: 'depot_strategie', label: 'Depot & Strategie' },
  { key: 'makro', label: 'Makro & Politik' },
  { key: 'sonstiges', label: 'Sonstiges' },
];

export const ABWEICHUNG_GRUENDE: { key: string; label: string }[] = [
  { key: 'anlass', label: 'Anlass' },
  { key: 'referent', label: 'Referent' },
  { key: 'aufzeichnung_fehlt', label: 'Aufzeichnung fehlt' },
  { key: 'test', label: 'Test' },
  { key: 'bauchgefuehl', label: 'Bauchgefühl' },
  { key: 'sonstiges', label: 'Sonstiges' },
];

export interface UpsellZeile {
  plx_product: string;
  name: string;
  order_page?: string | null;
  anfos: number;
  upsell_anfos: number;
  one_time_anfos: number;
  upsell_rate: number | null;
  first_order: string;
  last_order: string;
  /** Seite bietet nur den Jahresbezug an — Quote erzwungen, kein Kundenverhalten. */
  only_annual?: boolean;
  forced_anfos?: number;
  forced_upsell?: number;
  /** Produkt: Quote ohne Nur-Jahresbezug-Seiten. */
  upsell_rate_choice?: number | null;
  // P1-V3 § 2.6:
  value_per_anfo?: number | null;
  value_sum?: number;
  with_value?: number;
  pay_rate?: number | null;
  mature_anfos?: number;
  mature_paid?: number;
  /** Zeile „weitere Seiten unter 15 Anfos" (steht immer am Ende der Gruppe). */
  rest?: boolean;
  /** Eigene Seite: Webinar-Titel und -Datum statt Kennung. */
  webinar_title?: string | null;
  webinar_date?: string | null;
  wert_geschaetzt_anfos?: number;
}

export interface UpsellAntwort {
  window: Fenster;
  min_anfos_seite: number;
  products: UpsellZeile[];
  pages: UpsellZeile[];
  summary: { anfos: number; upsell_anfos: number; upsell_rate: number | null; upsell_rate_choice?: number | null; anfos_choice?: number; upsell_choice?: number };
  /** Upsell-Satz (live): Faktor Wert je Anfo mit ÷ ohne Upsell, Hebel je Prozentpunkt; Regel aus der API. */
  faktoren?: { plx_product: string; faktor: number | null; upsell_anfos: number; hebel_eur: number | null }[];
  faktor_regel?: { min_faktor: number; min_upsell_anfos: number };
}

export interface RegistrierungMonat {
  month: string;
  cohort: number;
  registered_before: number;
  within_window: number;
  later: number;
  never: number;
  /** Ohne Live-Anmeldung, aber binnen 30 Tagen nach Sendestart angemeldet (Aufzeichnung) — Teil von `never`, nicht in der Quote. */
  after_start_window: number;
  /** Davon in `within_window`: Live-Anmeldung (auch) über die Webinar-Karte der DOI-Danke-Seite (ab 12.10.2026). */
  within_window_doi: number;
  rate_window: number | null;
  is_mature: boolean;
  /** P1-V3 § 2.7: Webinare im 30-Tage-Fenster des Monats. */
  webinare_im_fenster?: number;
}

export interface RegistrierungAntwort {
  /** Kohorte: erster voller Monat bis zum Lead-Schnitt am PL-X-Datenstand (Audit 30.10.2026 B6a). */
  window: Fenster;
  /** Anmeldungen zählen bis hier (exklusiv); optional bis zum Deploy. */
  anmeldungen_bis?: string;
  days_window: number;
  months: RegistrierungMonat[];
  distribution: { key: string; label: string; n: number; share: number | null }[];
  median_days: number | null;
  distribution_months: string[];
  summary: {
    cohort: number;
    registered_before: number;
    within_window: number;
    later: number;
    after_start_window: number;
    within_window_doi: number;
    mature_cohort: number;
    mature_within_window: number;
    mature_registered_before: number;
    rate_window_mature: number | null;
  };
}

const WP = '/api/auswertungen/webinare-produkte';
/** Ohne Argument (Standard 12 Monate / alle Marken) dieselbe URL wie bisher — `useLaden(fetchProdukte)` lädt einmal. */
export const fetchProdukte = (zeitraum: ProdukteZeitraum = '12m') =>
  unwrap<ProdukteAntwort>(`${WP}/produkte${zeitraum === '12m' ? '' : `?zeitraum=${zeitraum}`}`);
export const fetchProduktEffizienz = (marken: EffizienzMarken = 'alle') =>
  unwrap<EffizienzAntwort>(`${WP}/effizienz${marken === 'alle' ? '' : `?marken=${marken}`}`);
/** Workshop E1 (29.10.2026): Umschalter der Karte „Was 1.000 Kontakte je Produkt erwarten lassen". */
export type EffizienzMarken = 'alle' | 'ft';

// --- Workshop P (29.10.2026): A2 „Was eine Anmeldung wert ist", W2 „Wenn ein Webinar wiederholt wird" ---
export interface WertJeAnmeldungZeile {
  /** Beworbenes Produkt; `''` = Termine ohne Produkt-Etikett. */
  plx_product: string;
  name: string;
  webinare: number;
  registrations: number;
  anfos: number;
  provisional: number;
  anfos_fest: number;
  anfos_ohne_anmeldung: number;
  anteil_ohne_anmeldung: number | null;
  wert_summe_fest: number;
  with_value_fest: number;
  cpa_summe_fest: number;
  with_cpa_fest: number;
  wert_je_anmeldung: number | null;
  budget_je_anmeldung: number | null;
  grau: boolean;
}
export interface WertJeAnmeldungAntwort {
  window: { asOf: string; von: string; bis: string; reife_tage: number };
  anfo_wert_stand: string;
  cpa_prozent: number | null;
  min_anfos: number;
  webinare_jung: number;
  rows: WertJeAnmeldungZeile[];
  summary: WertJeAnmeldungZeile;
}
export interface WiederholungLauf {
  webinar_id: string;
  title: string;
  plx_product: string | null;
  start_date: string;
  lauf: number;
  lauf_mindestens: boolean;
  pause_tage: number | null;
  hat_daten: boolean;
  sends: number;
  contacts: number;
  anmeldungen: number;
  klicks: number;
  anmeldungen_je_1k: number | null;
  klick_zu_anmeldung: number | null;
  grau: boolean;
}
export interface WiederholungGruppe {
  group_key: string;
  title: string;
  plx_product: string | null;
  product_name: string | null;
  erster_lauf: string;
  erster_lauf_vor_daten: boolean;
  laeufe_mit_daten: number;
  linie: boolean;
  laeufe: WiederholungLauf[];
}
export interface WiederholungAntwort {
  asOf: string;
  daten_ab: string | null;
  min_anmeldungen: number;
  min_laeufe_linie: number;
  termine: number;
  stichtag_hinweis: string | null;
  gruppen: WiederholungGruppe[];
}
export const fetchWertJeAnmeldung = () => unwrap<WertJeAnmeldungAntwort>(`${WP}/wert-je-anmeldung`);
export const fetchWiederholungen = () => unwrap<WiederholungAntwort>(`${WP}/wiederholungen`);
// --- Ende Workshop P ---
export const fetchWebinarRanking = () => unwrap<RankingAntwort>(`${WP}/webinare`);
/**
 * Kandidaten- und Ranking-Karte stehen an verschiedenen Stellen der Seite und lesen dieselbe Antwort —
 * ein gemeinsamer Abruf je Minute statt zweier gleicher Anfragen.
 */
let rankingGeteilt: { at: number; p: Promise<RankingAntwort> } | null = null;
export const fetchWebinarRankingGeteilt = (): Promise<RankingAntwort> => {
  if (!rankingGeteilt || Date.now() - rankingGeteilt.at > 60_000) {
    const p = fetchWebinarRanking();
    rankingGeteilt = { at: Date.now(), p };
    p.catch(() => {
      rankingGeteilt = null;
    });
  }
  return rankingGeteilt.p;
};
/** Nach einem Schreibvorgang: geteilten Abruf verwerfen, damit Kandidaten- UND Ranking-Karte neu laden. */
export const vergissRankingGeteilt = (): void => {
  rankingGeteilt = null;
};
export const fetchNachverfolgung = () => unwrap<NachverfolgungAntwort>(`${WP}/nachverfolgung`);

async function schreiben<T>(method: 'POST' | 'DELETE', url: string, body?: object): Promise<T> {
  const res = await fetchData<T>(url, { method, body: body ? JSON.stringify(body) : undefined });
  if (!res.success) throw new Error(res.error || 'Speichern fehlgeschlagen');
  vergissRankingGeteilt();
  return res.data as T;
}
export const setzeThema = (webinar_id: string, thema: string) => schreiben('POST', `${WP}/thema`, { webinar_id, thema });
export const holeKiThemen = () => schreiben<{ geschrieben: number; verworfen: number; ohne_ki: boolean }>('POST', `${WP}/themen/ki`);
export const setzeVorschlagSperre = (art: 'produkt' | 'gruppe', schluessel: string, grund: string | null) =>
  schreiben('POST', `${WP}/sperre`, { art, schluessel, grund });
export const hebeVorschlagSperreAuf = (art: 'produkt' | 'gruppe', schluessel: string) =>
  schreiben('DELETE', `${WP}/sperre?art=${encodeURIComponent(art)}&schluessel=${encodeURIComponent(schluessel)}`);
export const legeAnlassAn = (a: { datum: string; titel: string; thema: string | null; notiz: string | null }) => schreiben('POST', `${WP}/anlass`, a);
export const loescheAnlass = (id: number) => schreiben('DELETE', `${WP}/anlass/${id}`);
export const setzeAbweichung = (ziel_datum: string, grund: string, notiz: string | null) =>
  schreiben('POST', `${WP}/abweichung`, { ziel_datum, grund, notiz });

export const fetchUpsell = () => unwrap<UpsellAntwort>(`${WP}/upsell`);
export const fetchRegistrierung = () => unwrap<RegistrierungAntwort>(`${WP}/registrierung`);

// ─── Kapitel „Umsatz & Bezahlqualität" (P2, netto) ─────────────────────────────────────────

export interface KennzahlenZeile {
  jahr: number;
  von: string;
  bis: string;
  /**
   * Ende (EXKLUSIV) der reifen Spanne, über die `pay_rate` rechnet: 60 Tage vor dem Stichtag, in den Vorjahren dieselbe
   * Grenze um ein Jahr verschoben (Plan 2026-10-30-vergleichsfenster-drift); = `von`, solange noch keine Anfo reif ist.
   */
  reif_bis?: string;
  anfos: number;
  /** Vorläufig aus PX-Data ergänzt (Zahlstand unbekannt) — in `anfos` enthalten, in keiner Quote. */
  provisional: number;
  mature_anfos: number;
  mature_paid: number;
  pay_rate: number | null;
  paid_revenue: number;
  /** Bezahlt ÷ Anfos mit bekanntem Zahlstand. */
  paid_per_anfo: number | null;
  avg_age_days: number | null;
  // P2-V3 § 2.2 (K1):
  value_sum?: number;
  with_value?: number;
  without_value?: number;
  value_per_anfo?: number | null;
  payers?: number;
  projected_value?: number | null;
  first_buyer_share?: number | null;
  /** Gegen dasselbe Fenster ein Jahr früher (Anfos × Ø je Anfo); null vor 2024. */
  veraenderung?: Veraenderung | null;
  pay_rate_test?: ZeitraumTest | null;
  is_young?: boolean;
  young_share?: number | null;
}
export interface KennzahlenAntwort extends JungSchwellen {
  as_of: string;
  rows: KennzahlenZeile[];
  /** PL-X-Datenstand, an dem die Fenster enden (`plxDatenstand`). */
  datenstand?: string | null;
  /** K1-Lesezeile je Quartal des laufenden Jahres — dieselbe Backend-Funktion wie die K4a-Veränderung. */
  quartale?: { quarter: string; veraenderung: Veraenderung | null }[];
}

export interface QualitaetMonat {
  month: string;
  anfos: number;
  /** Vorläufig aus PX-Data ergänzt — Zahlstand unbekannt, eigener Topf; alle Töpfe zusammen = Anfos. */
  provisional: number;
  no_invoice: number;
  no_invoice_running: number;
  no_invoice_ended: number;
  invoiced_unpaid: number;
  trial_only: number;
  paid: number;
  /** Bezahlte ÷ Anfos mit bekanntem Zahlstand (ohne vorläufig ergänzte), Prozent. */
  paid_share: number | null;
  paid_revenue: number;
  is_young: boolean;
  young_share?: number | null;
}
/** K2 je Quartal (P2-V3 § 2.7), Monate als Kindzeilen. */
export interface QualitaetQuartal extends Omit<QualitaetMonat, 'month'> {
  quarter: string;
  months: QualitaetMonat[];
  /** Anteil ohne Rechnung — Etikett junger Zeilen („56 % noch ohne Rechnung"). */
  no_invoice_share?: number | null;
  abo_anfos?: number;
  withdrawn?: number;
  offener_betrag?: number | null;
}
export interface QualitaetAntwort extends JungSchwellen {
  window: { von: string; bis: string };
  reife_tage: number;
  months: QualitaetMonat[];
  summary: Omit<QualitaetMonat, 'month' | 'is_young'>;
  quarters?: QualitaetQuartal[];
  /** Summenzeile nur über reife Quartale (P2-V3 § 2.7). */
  reife_summe?: (Omit<QualitaetMonat, 'month' | 'is_young'> & { von: string; bis: string }) | null;
}

export interface KohorteZeile {
  cohort: string;
  anfos: number;
  /** Vorläufig aus PX-Data ergänzt — nicht in Bezahlquote und „bezahlt je Anfo". */
  provisional: number;
  avg_list_price: number | null;
  list_price_estimated_share: number | null;
  mature_anfos: number;
  mature_paid: number;
  pay_rate: number | null;
  payers: number;
  paid_revenue: number;
  paid_per_payer: number | null;
  paid_per_anfo: number | null;
  avg_age_days: number | null;
  is_young: boolean;
  // P2-V3 § 2.5 (K5):
  young_share?: number | null;
  value_per_anfo?: number | null;
  with_value?: number;
  /** „Läuft nach 6 Monaten noch" (Zähler/Basis Abo-Anfos); null, solange nicht alle Anfos 180 Tage alt sind. */
  laeuft_nach_6_monaten?: number | null;
  laeuft_zaehler?: number;
  laeuft_basis?: number;
  kuendigung_180?: number | null;
  kuendigung_zaehler?: number;
  kuendigung_basis?: number;
  /** Ø Quartalspreis laut Angebot, NETTO, nur damals erfasste Preise; null unter 50 % Abdeckung. */
  avg_list_price_net?: number | null;
  list_price_coverage?: number | null;
}
export interface KohortenAntwort extends JungSchwellen {
  window: { von: string; bis: string };
  reife_tage: number;
  cohorts: KohorteZeile[];
}

export interface PrognoseQuartal {
  quarter: string;
  anfos: number;
  /** Vorläufig aus PX-Data ergänzte Anfos (Zahlstand unbekannt) — nicht in „erreicht". */
  provisional: number;
  /** Anfos mit Wert bei Bestellung (vorläufige eingeschlossen). */
  with_value: number;
  /** Summe der Werte BEI BESTELLUNG (Angebot, sonst Produkt; netto). */
  expected_value: number;
  /** Hochrechnung aus den eigenen Zahlungen (m5), netto. Null für einen früheren Stichtag. */
  projected_value: number | null;
  paid_revenue: number;
  /** Bezahlt der Anfos mit Wert und bekanntem Zahlstand — Zähler von „erreicht". */
  paid_revenue_with_value: number;
  /** Wert bei Bestellung derselben Anfos — Nenner von „erreicht". */
  expected_value_reached_base: number;
  reached_share: number | null;
  avg_age_days: number | null;
  one_time: number;
  active_paying: number;
  paying_cancelled: number;
  running_unpaid: number;
  ended_paid: number;
  ended_unpaid: number;
  withdrawn: number;
  other_status: number;
  next_invoices: number;
  next_invoice_sum: number;
  next_invoice_expected: number;
  // P2-V3 § 2.6 (K4a, ein Fundament ohne vorläufige):
  with_value_fest?: number;
  payers?: number;
  veraenderung?: Veraenderung | null;
  is_young?: boolean;
  young_share?: number | null;
  /** Marken ¹ ² in der Zelle: ausgelassene vorläufige bzw. Einmalkäufe ohne Wert (mit deren Bezahlt-Betrag). */
  excluded_provisional?: number;
  excluded_without_value?: number;
  excluded_without_value_paid?: number;
  wert_geschaetzt_anfos?: number;
}
/** K4b je Vertragsstand (P2-V3 § 2.3). */
export interface VertragsstandZeile {
  key: 'laeuft_zahlt' | 'gekuendigt' | 'laeuft_ohne_zahlung';
  vertraege: number;
  mit_rechnung: number;
  summe: number;
  erwartet: number;
}
export interface PrognoseAntwort extends JungSchwellen {
  window: { von: string; bis: string };
  anfo_wert_stand: string;
  stichtag_hinweis: string | null;
  prognose: { von: string; bis: string; wochen: number };
  quarters: PrognoseQuartal[];
  summary: Omit<PrognoseQuartal, 'quarter' | 'avg_age_days'>;
  cashflow: { orders: number; sum_net: number; expected_net: number; outside_orders: number; outside_sum_net: number };
  vertragsstand?: VertragsstandZeile[];
  /** F5: Zahlungseingang dieses Quartal gegen Plan (nur %), Quartal nach Server-Uhr; null bei Fehler. */
  zahlungseingang?: { drift_pct: number | null; completed_weeks: number; total_weeks: number; quarter: string } | null;
}

export type KundentypKey = 'erstbesteller' | 'frueher_gezahlt' | 'frueher_nie_gezahlt' | 'serieller_nichtzahler';
export interface KundentypZeile {
  type: KundentypKey;
  anfos: number;
  share: number | null;
  mature_anfos: number;
  mature_paid: number;
  pay_rate: number | null;
  paid_revenue: number;
  paid_per_anfo: number | null;
  // P2-V3 § 2.4 (K3):
  /** Dieselbe Quote in den 12 Monaten davor + Zwei-Anteile-Test. */
  pay_rate_davor?: number | null;
  mature_anfos_davor?: number;
  mature_paid_davor?: number;
  test?: ZeitraumTest | null;
  payers?: number;
  avg_age_days?: number | null;
  /** Nur „früher bestellt, nie gezahlt": davon serielle Nichtzahler (`TERMS.seriellerNichtzahler.unterzeile`). */
  serial?: number;
}
export interface KundentypQuartalTyp {
  anfos: number;
  share: number | null;
  mature_anfos: number;
  mature_paid: number;
  pay_rate: number | null;
}
export interface KundentypAntwort {
  window: { von: string; bis: string };
  rows: KundentypZeile[];
  /** `anfos` = Anfos mit bekanntem Kundentyp; `provisional` = vorläufig ergänzte (ohne Adresse, nicht typisiert). */
  summary: {
    anfos: number;
    provisional: number;
    mature_anfos: number;
    mature_paid: number;
    pay_rate: number | null;
    duplicates: number;
    pay_rate_davor?: number | null;
    test?: ZeitraumTest | null;
    payers?: number;
    avg_age_days?: number | null;
  };
  quarters: {
    quarter: string;
    anfos: number;
    never_paid_before: number;
    serial: number;
    duplicates: number;
    is_young: boolean;
    /** P2-V3 § 2.4: alle drei Typen je Quartal (Grafik + Umschalter Anteil). */
    by_type?: Partial<Record<Exclude<KundentypKey, 'serieller_nichtzahler'>, KundentypQuartalTyp>>;
  }[];
  /** Quote heute mit der Typen-Mischung von vor 12 Monaten (Lesezeile „dieselbe Mischung …"). */
  mischung_quote?: number | null;
  /** F7: Bündel „Bisher bezahlt" nur vergleichbar, wenn das Ø Alter der Typen ≤ 30 Tage auseinanderliegt. */
  buendel_vergleichbar?: boolean;
  alter_spanne?: [number, number] | null;
}

const UM = '/api/auswertungen/umsatz';
export const fetchKennzahlen = () => unwrap<KennzahlenAntwort>(`${UM}/kennzahlen`);
export const fetchBezahlqualitaet = () => unwrap<QualitaetAntwort>(`${UM}/bezahlqualitaet`);
export const fetchKohorten = () => unwrap<KohortenAntwort>(`${UM}/kohorten`);
export const fetchPrognose = () => unwrap<PrognoseAntwort>(`${UM}/prognose`);
export const fetchKundentyp = () => unwrap<KundentypAntwort>(`${UM}/kundentyp`);

export interface AnmeldungMonat {
  month: string;
  leads: number;
  with_anfo: number;
  mature30: number;
  within30: number;
  rate30: number | null;
  mature90: number;
  within90: number;
  rate90: number | null;
  expected_per_lead: number | null;
  is_young: boolean;
}
export interface AnmeldungAntwort {
  window: { von: string; bis: string };
  /** Stand der Wert-Quelle (gleich `anfo_wert_stand`), z. B. „Stand heute“ oder „M5-Lauf vom …“. */
  cpa_stand: string;
  anfo_wert_stand: string;
  kennzahlen: {
    leads: number;
    leads_with_anfo: number;
    anfos: number;
    mature30: number;
    within30: number;
    rate30: number | null;
    mature90: number;
    within90: number;
    rate90: number | null;
    paid_revenue: number;
    expected_value: number;
    cpa_value: number;
    paid_per_lead: number | null;
    expected_per_lead: number | null;
    cpa_per_lead: number | null;
    first_mature: number;
    first_paid: number;
    first_pay_rate: number | null;
  };
  verlauf: AnmeldungMonat[];
  verteilung: {
    reife_tage: number;
    leads: number;
    with_anfo: number;
    eimer: { key: string; label: string; n: number; share: number | null }[];
    median_days: number | null;
  };
  produkte: {
    plx_product: string;
    name: string | null;
    n: number;
    share: number | null;
    mature: number;
    paid: number;
    pay_rate: number | null;
    /** P2-V3 § 2.9 A4: Ø Erwarteter Wert der ersten Anfo. */
    expected_value?: number | null;
  }[];
  // P2-V3 § 2.9:
  /** Ein Schnitt für Leads und Anfos: PL-X-Datenstand; dazu der Stand der Leads. */
  schnitt?: string | null;
  leads_stand?: string | null;
  quartale?: AnmeldungQuartal[];
  /** A4-Umschalter „nach Kanal der ersten Anfo". */
  kanaele?: { channel: string; label: string; n: number; share: number | null; mature: number; paid: number; pay_rate: number | null; expected_value?: number | null }[];
  /** Anteil Einkaufspreis ÷ Erwarteter Wert (`cpa_per_lead / expected_per_lead`), aus der API. */
  cpa_anteil?: number | null;
}
/** A2 je Anmeldequartal, Monate als Kindzeilen; Veränderung erst nach F8. */
export interface AnmeldungQuartal {
  quarter: string;
  leads: number;
  mature30: number;
  within30: number;
  rate30: number | null;
  mature90: number;
  within90: number;
  rate90: number | null;
  expected_per_lead: number | null;
  /** Summe Erwarteter Wert der ersten 90 Tage der Leads, die schon 90 Tage dabei sind. */
  expected_sum?: number | null;
  with_anfo: number;
  is_young: boolean;
  months: AnmeldungMonat[];
  veraenderung_leads?: number | null;
}
export const fetchAnmeldungBestellung = () => unwrap<AnmeldungAntwort>(`${UM}/anmeldung-bestellung`);

/** Eine Zahlungsart eines Produkts im wirksamen Lauf, alles brutto (Modellgrößen). */
export interface AnfoWertZelle {
  n: number;
  sum_brutto: number;
  mean_paid_brutto: number | null;
  start_brutto: number;
  start_source: 'poolMean' | 'countedPrices' | 'knownPrices' | 'knownPricesOld' | 'offerConfig' | 'poolCellMean' | null;
  r_brutto: number;
  g: number;
  value_brutto: number;
}
export interface AnfoWertProdukt {
  plx_product: string;
  name: string | null;
  kind: 'abo' | 'einmal' | 'neu';
  netto_faktor: number;
  value_brutto: number | null;
  value_netto: number | null;
  cpa_netto: number | null;
  run_value_brutto: number | null;
  rechnerisch_brutto: number | null;
  share_sofort: number | null;
  n365: number | null;
  s365: number | null;
  has_sofort_offer: boolean | null;
  has_trial_offer: boolean | null;
  T: AnfoWertZelle | null;
  S: AnfoWertZelle | null;
  onetime_p: number | null;
  onetime_price: number | null;
}
export interface AnfoWertAngebot {
  werbeaktion_wbkz: string;
  plx_product: string;
  zahlungsart: 'T' | 'S';
  first_amount: number | null;
  n: number | null;
  start_brutto: number | null;
  r_brutto: number | null;
  g: number | null;
  value_brutto: number;
  value_netto: number;
  netto_faktor: number;
  run_value_brutto: number | null;
  neu_bewertet: boolean;
  run_id: number;
  /** Marke „geschätzt": Sofortzahlung über dem m5-Preisdeckel (`wertUeberPreisdeckel`). */
  wert_geschaetzt?: boolean;
}
export interface AnfoWertBestellungen {
  plx_product: string;
  name: string | null;
  netto_faktor: number;
  n: number;
  n_null: number;
  avg_netto: number | null;
  sum_netto: number;
  sum_brutto: number;
}
export interface AnfoWertAntwort {
  model: 'm4' | 'm5';
  /** true = Rückweg m4 (oder Lauf ohne m5-Parameter): nur die Produkttabelle. */
  fallback: boolean;
  run: { id: number; as_of: string; mode: string; model_version: string } | null;
  last_run: { id: number; as_of: string; mode: string } | null;
  pct: number | null;
  kopf: { model_version: string; g_t: number; g_s: number; g_q: number; g_u: number; k: number; start_t_brutto: number } | null;
  produkte: AnfoWertProdukt[];
  angebote: AnfoWertAngebot[];
  bestellungen: AnfoWertBestellungen[];
  bestellungen_summe: { n: number; n_null: number; avg_netto: number | null; sum_netto: number };
}
export const fetchAnfoWertModell = () => unwrap<AnfoWertAntwort>(`${UM}/anfo-wert`);

/** `2026-Q3` → `3. Q. 26`; `2026-H1` → `1. Hj. 26`. */
export function formatPeriode(p: string): string {
  const q = /^(\d{4})-Q(\d)$/.exec(p);
  if (q) return `${q[2]}. Q. ${q[1].slice(2)}`;
  const h = /^(\d{4})-H(\d)$/.exec(p);
  if (h) return `${h[2]}. Hj. ${h[1].slice(2)}`;
  return p;
}

/** `2026-10` → `Okt 26`. */
export function formatMonat(month: string): string {
  const namen = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  return m ? `${namen[Number(m[2]) - 1]} ${m[1].slice(2)}` : month;
}

// ─── Webinar-Detailseite ─────────────────────────────────────────────────────────────────────
// Dieselben Antworten wie /api/webinar-statistics/* (WebinarEvaluationService), im Hausformat.
const WD = (id: string) => `/api/auswertungen/webinare/${encodeURIComponent(id)}`;
export const fetchDetailEngagement = (id: string) =>
  unwrap<WebinarEngagementDetailData>(`${WD(id)}/engagement`);
export const fetchDetailAnfos = (id: string) => unwrap<WebinarOrderDetailData[]>(`${WD(id)}/anfos`);
export const fetchDetailRegistrierungen = (id: string) =>
  unwrap<WebinarRegistrationData[]>(`${WD(id)}/registrierungen`);
export const fetchDetailZuordnung = (id: string) =>
  unwrap<WebinarMappingBreakdownData>(`${WD(id)}/zuordnung`);
export const fetchDetailGruppierung = (id: string) =>
  unwrap<WebinarGroupedEvaluationDetailData>(`${WD(id)}/gruppierung`);
export const fetchDetailRaum = (id: string) => unwrap<RoomRetentionData>(`${WD(id)}/raum`);
export const fetchDetailVergleich = (id: string) =>
  unwrap<WebinarComparisonData>(`${WD(id)}/vergleich`);

// ─── Kapitel „Kanäle & Werbeflächen" (P3) ─────────────────────────────────────────────────────
// Backend: /api/auswertungen/kanaele/* (services/auswertungen/kanaeleService.ts, werbeflaechenService.ts).
const KA = '/api/auswertungen/kanaele';

/** Wählbare Fenster in Monaten (Till 20.10.2026: Standard 6). */
export const KANAL_FENSTER = [3, 6, 12] as const;
export type KanalFensterMonate = (typeof KANAL_FENSTER)[number];

export type KanalGruppeKey = 'webinar' | 'direct_mailing' | 'newsletter' | 'doi' | 'ads' | 'welcome' | 'other';

export interface KanalKennzahlen {
  anfos: number;
  share: number | null;
  provisional: number;
  mature_anfos: number;
  mature_paid: number;
  pay_rate: number | null;
  payers: number;
  paid_revenue: number;
  paid_per_anfo: number | null;
  value_per_anfo: number | null;
  with_value: number;
  avg_age_days: number | null;
  customer: { erstbesteller: number; frueher_gezahlt: number; frueher_nie_gezahlt: number };
  never_paid_share: number | null;
  serial_nonpayer: number;
  /** Erwarteter Wert (bei Bestellung), Summe € netto über die nicht vorläufigen Anfos; ohne Wert = 0 €. */
  value_sum: number;
  /** Anfos mit Kundentyp (= nicht vorläufige) — Nenner von first_buyer_share / never_paid_share. */
  typed: number;
  /** Anteil Erstbesteller in % (ungerundet); null bei typed = 0. */
  first_buyer_share: number | null;
  /** Veränderung des erwarteten Werts gegen beide Basen; die Fenster stehen in `vergleich` der Antwort. */
  veraenderung: VeraenderungZeile;
  /** ▲/▼ an der Bezahlquote: roher p-Wert; null = nicht getestet (unter der Schwelle) und in total. */
  pay_rate_p: number | null;
  /** Holm-korrigiert über die feste Testfamilie der Karte. */
  pay_rate_p_holm: number | null;
  pay_rate_flag: 'up' | 'down' | null;
}

/** Die zwei Vergleichsbasen der Veränderung (Till 23.10.2026, E6). */
export type VergleichsBasis = 'vorjahr' | 'vorher';

export interface Zeitfenster {
  /** inklusive, `YYYY-MM-DD HH:mm:ss` */
  von: string;
  /** EXKLUSIV */
  bis: string;
  erster_tag: string;
  letzter_tag: string;
}

export interface VergleichsFenster {
  /** PL-X-Datenstand (jüngste Bestellung, meist der Vorabend); null, wenn keiner ermittelbar war. */
  datenstand: string | null;
  jetzt: Zeitfenster;
  vorjahr: Zeitfenster;
  vorher: Zeitfenster;
  /** Länge des Kartenfensters in Tagen; bei 12 Monaten ist vorher == vorjahr. */
  tage: number;
}

/** Zahlen eines Vergleichsfensters, alle OHNE vorläufige Anfos (`provisional` nur fürs Tooltip). */
export interface VergleichsWerte {
  anfos: number;
  provisional: number;
  with_value: number;
  without_value: number;
  value_sum: number;
  value_per_anfo: number | null;
}

/** Veränderung gegen EINE Basis; Prozente ungerundet. */
export interface Veraenderung {
  basis: VergleichsWerte;
  /** (1 + ΔAnfos) × (1 + ΔØ je Anfo) − 1; null = neu oder nicht rechenbar; −100, wenn jetzt 0 Anfos. */
  delta: number | null;
  delta_anfos: number | null;
  delta_je_anfo: number | null;
  /** Vergleichsfenster ohne Anfo, jetzt mit Anfos → „neu". */
  neu: boolean;
  grau_grund: 'wenig_anfos' | null;
}

export interface VeraenderungZeile {
  jetzt: VergleichsWerte;
  vorjahr: Veraenderung;
  vorher: Veraenderung;
}

/** Grau-Schwellen der Seite (V3 §2.0/3) — Tooltips daraus bauen, keine Zahl hart codieren. */
export interface GrauSchwellen {
  /** Bezahlquote und bezahlt je Anfo grau unter so vielen Anfos ab `reife_tage` Tagen. */
  bezahlquote_reife_anfos: number;
  /** Erstbesteller-Anteil und Ø je Anfo grau unter so vielen Anfos. */
  anteil_anfos: number;
  veraenderung_anfos: number;
  markierung_reife_anfos: number;
}

export interface KanalZeileF6 extends KanalKennzahlen {
  key: string;
  label: string;
  description: string;
}
export interface KanalGruppeF6 extends KanalKennzahlen {
  key: KanalGruppeKey;
  label: string;
  description: string;
  channels: KanalZeileF6[];
}
export interface KanalFensterInfo {
  asOf: string;
  monate: number;
  von: string;
  bis: string;
}
export interface KanaeleAntwort {
  window: KanalFensterInfo;
  reife_tage: number;
  anfo_wert_stand: string;
  vergleich: VergleichsFenster;
  grau_schwellen: GrauSchwellen;
  markierung_alpha: number;
  total: KanalKennzahlen;
  groups: KanalGruppeF6[];
}
export interface VerlaufMonat {
  month: string;
  is_partial: boolean;
  is_young: boolean;
  until: string;
}
export interface VerlaufZeile {
  month: string;
  channel: string;
  anfos: number;
  provisional: number;
  value_sum: number;
  without_value: number;
  paid_revenue: number;
  payers: number;
}
export interface QuartalFenster {
  label: string;
  von: string;
  /** exklusiv, YYYY-MM-DD */
  bis: string;
  months: string[];
}
/** Vergleichszeile über dem Verlauf: letztes volles Quartal gegen Vorjahresquartal und Vorquartal. */
export interface VerlaufVergleich {
  quartal: QuartalFenster;
  vorjahr_quartal: QuartalFenster;
  vorher_quartal: QuartalFenster;
  /** `anfos_gesamt` inkl. vorläufiger (= Balkenhöhe). */
  jetzt: VergleichsWerte & { anfos_gesamt: number };
  vorjahr: Veraenderung;
  vorher: Veraenderung;
}
export interface VerlaufAntwort {
  window: { von: string; bis: string; monate: number };
  vergleich: VerlaufVergleich;
  grau_schwellen: GrauSchwellen;
  months: VerlaufMonat[];
  channels: { key: string; label: string; group: KanalGruppeKey }[];
  groups: { key: KanalGruppeKey; label: string; channels: string[] }[];
  rows: VerlaufZeile[];
  anfo_wert_stand: string;
  /** Workshop-Lücke E2: Vorjahresmonat des laufenden Monats, auf denselben Tag geschnitten (optional bis zum Deploy). */
  vorjahr_teilmonat?: VorjahrTeilmonat;
  /**
   * PL-X-Datenstand (`YYYY-MM-DD HH:mm:ss`), bis zu dem der laufende Monat zählt (Audit 30.10.2026 B5/C1); optional bis
   * zum Deploy.
   */
  datenstand?: string | null;
}
/** Vorjahres-Teilmonat (E2): `month` = Vorjahresmonat, `until` = letzter gezählter Tag, `zu_month` = laufender Monat. */
export interface VorjahrTeilmonat {
  month: string;
  until: string;
  zu_month: string;
  rows: VerlaufZeile[];
}
export interface BlogZeile extends KanalKennzahlen {
  blog: string;
  /** Restzeile („Kennung ohne Marke", „Ohne Kennung") — immer unten; statt Namensvergleich verwenden. */
  rest: boolean;
}
export interface BlogsAntwort {
  window: KanalFensterInfo;
  reife_tage: number;
  vergleich: VergleichsFenster;
  grau_schwellen: GrauSchwellen;
  markierung_alpha: number;
  total: KanalKennzahlen;
  rows: BlogZeile[];
}
/**
 * Werbeflächen (GET /api/auswertungen/kanaele/werbeflaechen) nach dem KPI-Review 23.10.2026: EINE Antwort für zwei
 * Karten (E2-A) — „Webinar-Einladungen" = `block === 'anmeldung'`, „Direkte Bestell-Links" = `'bestellung'`.
 * `*_je_1000` je 1.000 Kontakte; Anteile als Bruch 0..1; Euro netto; `null` = nicht messbar.
 */
export type FlaechenBlock = 'anmeldung' | 'bestellung';

/** Grau-Regel der Werbeflächen-Karten (vorschlag_v3 §2.0/3). Zählungen werden nie grau. */
export interface FlaechenGrauSchwellen {
  /** Wert/Anfos je 1.000, Ø je Anfo, Anfos je 100 Anm./Teiln. (Anfos OHNE vorläufige). */
  anfos: number;
  /** Anmeldungen je 1.000 (`trichter.anmeldungen`). */
  anmeldungen: number;
  /** Show-up, Teilnehmer je 1.000, Show-up-Index (`trichter.rechtzeitig`). */
  rechtzeitig: number;
  /** Abmeldungen je 1.000; Wert je Abmeldung grau unter `anfos` Anfos ODER unter `abmeldungen`. */
  abmeldungen: number;
  /** Unterzeile „x % erst nach Beginn" nur ab so vielen `trichter.nach_beginn.anmeldungen`. */
  nach_beginn_anmeldungen: number;
}

/** Show-up einer Messära. */
export interface AeraWert {
  rechtzeitig: number;
  teilnehmer: number;
  show_up: number | null;
}

/** Block A, Zähleinheit: PERSONEN an ihrer ersten Anmeldung je Webinar (E5-A); „E10-Basis" = Webinare ohne Tracking-Warnung. */
export interface EinladungsTrichter {
  webinare: number;
  /** Nenner der E10-Werte; null ohne Kontaktzahl oder wenn die Kontakte nicht nach Webinar trennbar sind. */
  kontakte: number | null;
  anmeldungen: number;
  anmeldungen_je_1000: number | null;
  /** true → Wert über ALLE Webinare und alle Kontakte (ⓘ², heute nur die Automatische Anzeige). */
  anmeldungen_alle_webinare: boolean;
  rechtzeitig: number;
  teilnehmer_rechtzeitig: number;
  show_up: number | null;
  teilnehmer: number;
  teilnehmer_je_1000: number | null;
  teilnehmer_je_1000_grund: string | null;
  anfos_teilnehmer: number;
  anfos_je_100_teilnehmer: number | null;
  show_up_index: number | null;
  raum_anteil: number | null;
  aera: { frueheres_werkzeug: AeraWert; uebergang: AeraWert; raum: AeraWert };
  live_kaeufer_ohne_signal: number | null;
  /** ALLE Webinare (inkl. Warn-Webinare). */
  gesamt: {
    anmeldungen: number;
    nach_beginn: number;
    rechtzeitig: number;
    anmeldungen_je_1000: number | null;
    anfos_live: number;
    anfos_je_100_anmeldungen: number | null;
  };
  /** Die ausgeschlossenen Warn-Webinare dieser Fläche. */
  ohne_teilnahmedaten: {
    anmeldungen: number;
    rechtzeitig: number;
    webinare: Array<{ webinar_id: string; start: string | null }>;
  };
  /** Nur Webinare ab `nach_beginn_erfasst_ab` (alle, auch Warn). */
  nach_beginn: { webinare: number; anmeldungen: number; nach_beginn: number; anteil: number | null; anfos: number };
}

/** Vergleichszeile der ganzen Liste „je zugestellter Mail". */
export interface JeZustellung {
  kontakte: number;
  anmeldungen_je_1000: number | null;
  teilnehmer_je_1000: number | null;
  anfos_je_1000: number | null;
  wert_je_1000: number | null;
  abmeldungen_je_1000: number | null;
}

export interface FlaechenZeile {
  key: string;
  label: string;
  block: FlaechenBlock;
  /** Zeilen-ⓘ; beginnt im Frontend mit `GLOSSAR[glossar]`. */
  beschreibung: string;
  glossar: 'heatUp' | 'redlink' | 'direktMailing' | 'nachfass' | null;
  test: boolean;
  beendet_am: string | null;
  daten_ab: string | null;
  erster_versand_gesamt: string | null;
  /** daten_ab ?? erster_versand_gesamt; null = Beginn unbekannt (Datengrenze der Quelle). */
  laeuft_seit: string | null;
  nenner: string | null;
  ohne_nenner_grund: string | null;
  versaende: number;
  /** Zugestellte Mails (ganze Liste: nur aktive); null = keine Kontaktzahl. */
  kontakte: number | null;
  /** Nur ganze Liste: alle Zustellungen. */
  kontakte_alle: number | null;
  anfos_je_1000_alle: number | null;
  /** Erster/letzter Versandtag IM FENSTER (für `teilzeitraum()`). */
  erster_versand: string | null;
  letzter_versand: string | null;
  /** Alle Anfos inkl. vorläufige. */
  anfos: number;
  provisional: number;
  /** Zähler jeder Quote und der Unterzeile „N Anfos". */
  anfos_ohne_vorlaeufige: number;
  anfos_je_1000: number | null;
  wert_summe: number;
  anfos_ohne_wert: number;
  wert_je_1000: number | null;
  abmeldungen: number | null;
  abmeldungen_je_1000: number | null;
  abmeldungen_grund: string | null;
  wert_je_abmeldung: number | null;
  // Zeilen-Einheit von vor dem KPI-Review (Heat-Ups je Anmeldezeile); die Karten lesen `trichter`.
  anmeldungen_live: number | null;
  anmeldungen_nach_start: number | null;
  anmeldungen_je_1000: number | null;
  anfos_live: number | null;
  anfos_je_100_anmeldungen: number | null;
  /** Block A; null bei Block B und (bis zum ersten Lauf des neuen Schreibers) bei den Versand-Cache-Flächen. */
  trichter: EinladungsTrichter | null;
  je_zustellung: JeZustellung | null;
  wenig_daten: boolean;
  /** Nachfass: Abschnitt „Nur an Webinar-Angemeldete verschickt …". */
  warm: boolean;
}

export interface DirektvergleichSeite {
  kontakte: number;
  anmeldungen: number;
  rechtzeitig: number;
  teilnehmer_rechtzeitig: number;
  teilnehmer: number;
  anfos: number;
  wert: number;
  show_up: number | null;
  anmeldungen_je_1000: number | null;
  teilnehmer_je_1000: number | null;
  anfos_je_1000: number | null;
  wert_je_1000: number | null;
}

interface DirektvergleichWebinarSeite {
  teilnehmer_je_1000: number | null;
  anfos_je_1000: number | null;
  anmeldungen_je_1000: number | null;
}

/** E11: Vortag gegen Webinartag auf denselben Webinaren. */
export interface Direktvergleich {
  anzahl: number;
  mindestens: number;
  /** anzahl ≥ mindestens — sonst Zeile nicht zeigen. */
  sichtbar: boolean;
  vortag: DirektvergleichSeite;
  webinartag: DirektvergleichSeite;
  vortag_vorn: number;
  webinare: Array<{ webinar_id: string; start: string | null; vortag: DirektvergleichWebinarSeite; webinartag: DirektvergleichWebinarSeite }>;
}

/** Daten für den Hinweis unter der Einladungs-Karte. */
export interface HinweisTeilnahme {
  warn_webinare: Array<{ webinar_id: string; start: string | null; anmeldungen: number }>;
  anmeldungen: number;
  messluecke_webinare: Array<{ webinar_id: string; start: string | null; show_up: number | null }>;
}

export interface WerbeflaechenAntwort {
  /** von/bis: Bestellfenster [von, bis); versand_bis: Versände VOR diesem Tag (14 Tage Reife). */
  window: { von: string; bis: string; versand_bis: string; monate: 3 | 6 | 12; asOf: string };
  reife_versand_tage: number;
  wenig_daten_anfos: number;
  grau_schwellen: FlaechenGrauSchwellen;
  newsletter_daten_ab: string;
  nach_beginn_erfasst_ab: string;
  raum_messung_ab: string;
  raum_uebergang_ab: string;
  live_berechnet: boolean;
  /** false bis Deploy + erster Lauf des neuen Schreibers. */
  personen_berechnet: boolean;
  anfo_wert_stand: string;
  stichtag_hinweis: string | null;
  rows: FlaechenZeile[];
  direktvergleich: Direktvergleich | null;
  hinweis_teilnahme: HinweisTeilnahme;
}

export const fetchKanaele = (m: KanalFensterMonate) => unwrap<KanaeleAntwort>(`${KA}/kanaele?monate=${m}`);
export const fetchKanalVerlauf = () => unwrap<VerlaufAntwort>(`${KA}/verlauf`);
export const fetchBlogs = (m: KanalFensterMonate) => unwrap<BlogsAntwort>(`${KA}/blogs?monate=${m}`);
export const fetchWerbeflaechen = (m: KanalFensterMonate) => unwrap<WerbeflaechenAntwort>(`${KA}/werbeflaechen?monate=${m}`);

/** Bezugsart der Anfos eines Webinars (Sektions-Detailseite). */
export type BezugsartKey = 'upsell' | 'quartalsbezug' | 'einmalkauf' | 'offen' | 'vorlaeufig';
export interface BezugsartAntwort {
  anfos: number;
  rows: { art: BezugsartKey; anfos: number; preis: number | null; ohne_testphase: boolean }[];
}
export const fetchDetailBezugsart = (id: string) => unwrap<BezugsartAntwort>(`${WD(id)}/bezugsart`);

// --- P1 --- (weitere Antworttypen des P1-Bauers, nur additiv; Datei vor jeder Änderung neu lesen)

// P1-Umbau (Plan 2026-10-23-p1-p2-kpi-review-umbau): Ergänzungen per Interface-Verschmelzung — alle optional, damit die
// Karten ein älteres Backend (Deploy-Reihenfolge, Cache) aushalten.
export interface WebinarZeile {
  /** Erwarteter Wert je 1.000 Kontakte, nur Käufe über die Einladung, Wert je Anfo ohne vorläufige (wie die Effizienz). */
  wert_je_1k_fest?: number | null;
  /** Erstkontakt-Anfos ohne vorläufige — Zähler des Werts je 1.000 (Codex-Befund 25.10.). */
  send_anfos_fest?: number;
}
export interface EffizienzTeil {
  /** Davon vorläufig (nur Direkt-Mailing); in keiner Quote und keinem Euro-Wert. */
  provisional?: number;
  /** Ø Alter der Anfos ohne vorläufige (Bündel „Bisher bezahlt je 1.000", Persona-Check B70). */
  avg_age_days?: number | null;
  age_n?: number;
}
export interface ProduktZeile {
  first_buyer_share?: number | null;
  /** Erwarteter Wert der Anfos aus dem Kanal Webinar (Grafik, Umschalter „Erwarteter Wert"). */
  webinar_value_sum?: number;
}
export interface ProdukteAntwort {
  /** Seitenkopf (E1): Erwarteter Wert Kanal Webinar gegen alle Anfos der 12 Monate, ohne vorläufige. */
  kanal_webinar?: { wert: number; gesamt: number };
}
export interface ShowUpAntwort {
  typisch_show_up?: number | null;
  typisch_termine?: number;
  /** Ab hier sind Anmeldungen nach Beginn getrennt gespeichert. */
  nach_beginn_ab?: string;
}
export interface EmpfehlungKopf {
  /** K7: Rechenverfahren des Schnappschusses; Euro-Werte nur bei 'bestellung' mit den heutigen vergleichbar. */
  wertbasis?: 'bestellung' | 'alt';
}
export interface PrognoseKandidat {
  /** Log-Skala der Prognose und ihre Schätzunsicherheit — für „Chance besser als Platz 1" (Wahl-Spalte). */
  mu?: number | null;
  v?: number | null;
}
/** Effizienz-Summenzeile mit der Obergrenze (E3). */
export type EffizienzSumme = EffizienzAntwort['summary'] & { obergrenze_per_1k?: number | null; obergrenze_anfos?: number };
/** Upsell-Summe mit dem Ø Erwarteten Wert je Anfo. */
export type UpsellSumme = UpsellAntwort['summary'] & { value_per_anfo?: number | null };

/** Termin-Kennzahlen der Sektions-Detailseite (dieselbe Rechnung wie die Webinar-Tabelle, K6). */
export interface DetailKennzahlen {
  webinar_id: string;
  public_title: string;
  plx_product: string | null;
  product_name: string | null;
  start_date: string;
  wert_summe_fest: number;
  with_value_fest: number;
  anfos: number;
  provisional: number;
  wert_geschaetzt_anfos: number;
  contacts: number;
  send_anfos: number;
  wert_je_1k_fest: number | null;
  unsubscribes: number;
  unsubscribes_per_1k: number | null;
  registrations: number;
  registrations_after_start: number;
  zahlen_offen_bis: string | null;
  paid_revenue?: number;
  payers?: number;
  avg_age_days?: number | null;
  /** Show-up mit derselben Unterdrückung wie die Kapitelseite (Messlücke, Tracking-Warnung, übernommen). */
  show_up?: ShowUpZeile;
  /** Anfos ohne vorläufige, deren Hochrechnung aus eigenen Zahlungen stammt; 0 = gleich dem Erwarteten Wert (B17). */
  anfos_aus_zahlung?: number;
  /**
   * Bestellungen des Webinars inkl. der von PL-X verweigerten und davon verweigert (Menge der Seite Bestellungen);
   * bestellungen − verweigert = anfos.
   */
  verweigert: { bestellungen: number; verweigert: number } | null;
}

/**
 * Persona-Umbau B9 (28.10.2026): „typisch" der Detail-Kacheln = die Zeile „Typischer Termin" der Webinar-Tabelle — aus
 * DERSELBEN Antwort (`fetchWebinarRankingGeteilt().typisch`), Show-up aus der Teilnahme-Antwort (`typisch_show_up`).
 * Eigener Abruf, damit die Kacheln nicht auf das Ranking warten (kalt ~2,4 s zusätzlich, gemessen 28.10.).
 */
export type TypischDetail = TypischerTermin & { show_up_termine: number | null };
export const fetchTypischerTermin = async (): Promise<TypischerTermin | null> => (await fetchWebinarRankingGeteilt()).typisch ?? null;

/** Stand der Webinar-Zuordnung zur Rechenzeit der Ranking-Antwort (Persona-Check B14/B52). */
export interface ZuordnungStand {
  /** Letzter Lauf, Berliner Zeit; null = unbekannt. */
  lauf: string | null;
  /** Bestellungen mit Webinar-Kennung nach dem letzten Lauf — noch bei keinem Termin gezählt. */
  offen: number;
  offen_bis: string | null;
  /** Je Termin: wie viele dieser Bestellungen voraussichtlich zu ihm gehören. */
  je_termin: Record<string, number>;
}
export interface RankingAntwort {
  zuordnung?: ZuordnungStand;
}

export const fetchTeilnahme = () => unwrap<ShowUpAntwort>(`${WP}/teilnahme`);
export const fetchDetailKennzahlen = (id: string) => unwrap<DetailKennzahlen>(`/api/auswertungen/webinare/${encodeURIComponent(id)}/kennzahlen`);

/**
 * „Typisch" der Detailseite gegen die 12 Monate VOR dem Termin (Plan 2026-10-30-vergleichsfenster-drift): Mediane der
 * fertigen Termine in [Beginn − 12 M, Beginn), Anfos bis heute; je Kennzahl erst ab `min_termine` Fällen.
 */
export interface TypischVorTermin {
  fenster: { von: string; bis: string };
  typisch: TypischerTermin | null;
  termine_kontakte: number;
  termine_je_1k: number;
  show_up_termine: number;
  /** Anteil „früher bestellt, nie gezahlt" an den Anfos derselben Spanne, Prozent; null unter `min_anfos`. */
  anteil_nie_gezahlt: number | null;
  anfos_anteil: number;
  min_termine: number;
  min_anfos: number;
}
export const fetchTypischVorTermin = (id: string) => unwrap<TypischVorTermin>(`/api/auswertungen/webinare/${encodeURIComponent(id)}/typisch`);

// --- P2 --- (weitere Antworttypen des P2-Bauers, nur additiv; Datei vor jeder Änderung neu lesen)
// Felder der P2-Antworten nach dem Umbau (Backend `umsatzService.ts`, 25.10.2026), die die Stufe-0-Typen oben noch
// nicht kennen. Die Karten lesen die `…P2`-Typen über die `…P2`-Fetcher; die Typen oben bleiben unverändert.
export interface KennzahlenAntwortP2 extends KennzahlenAntwort {
  quartale?: { quarter: string; veraenderung: Veraenderung | null; laufend?: boolean; anfos?: number; value_sum?: number }[];
}
export type QualitaetZeileP2 = Omit<QualitaetQuartal, 'quarter' | 'months'> & { key: string; quarter?: string; month?: string; months?: QualitaetZeileP2[] };
export interface QualitaetAntwortP2 extends QualitaetAntwort {
  datenstand?: string | null;
  reife_summe?: (Omit<QualitaetMonat, 'month' | 'is_young'> & { von: string; bis: string; quartale?: number; no_invoice_share?: number | null; abo_anfos?: number; withdrawn?: number; offener_betrag?: number | null }) | null;
}
export interface KohorteZeileP2 extends KohorteZeile {
  beides?: number;
  youngest_age_days?: number | null;
  preis_luecken?: string[];
}
export interface KohortenAntwortP2 extends Omit<KohortenAntwort, 'cohorts'> {
  datenstand?: string | null;
  bindung_tage?: number;
  preis_min_abdeckung?: number;
  cohorts: KohorteZeileP2[];
}
export interface PrognoseQuartalP2 extends PrognoseQuartal {
  expected_value_reached_base: number;
  avg_age_base?: number | null;
  excluded_provisional_value?: number;
  excluded_without_value_payers?: number;
}
export interface PrognoseAntwortP2 extends Omit<PrognoseAntwort, 'quarters' | 'summary'> {
  datenstand?: string | null;
  quarters: PrognoseQuartalP2[];
  summary: Omit<PrognoseQuartalP2, 'quarter' | 'avg_age_days'>;
  ohne_naechste_rechnung?: { gesamt: number; rechnung_unbezahlt: number; faellig_unverbucht?: number; abrechnung_veraltet?: number };
}
/** F5: Zahlungseingang dieses Quartal gegen Plan — eigene Route (computeCashflowHistory kalt 12–19 s). */
export interface ZahlungseingangAntwort {
  quarter: string;
  drift_pct: number | null;
  completed_weeks: number;
  total_weeks: number;
  baseline_complete: boolean;
  stichtag_hinweis: string | null;
}
export interface KundentypZeileP2 extends KundentypZeile {
  young_share?: number | null;
  is_young?: boolean;
}
export interface KundentypAntwortP2 extends Omit<KundentypAntwort, 'rows'>, JungSchwellen {
  rows: KundentypZeileP2[];
  window_davor?: { von: string; bis: string };
  /** Reife Spannen der Quote jetzt und davor (gleich lang, ein Jahr auseinander; Plan 2026-10-30). */
  window_reif?: Zeitfenster | null;
  window_davor_reif?: Zeitfenster | null;
  datenstand?: string | null;
  alter_max_abstand?: number;
}
export interface AnmeldungMonatP2 extends AnmeldungMonat {
  expected_sum?: number | null;
}
export interface AnmeldungQuartalP2 extends Omit<AnmeldungQuartal, 'months'> {
  months: AnmeldungMonatP2[];
}
export interface ErsteAnfoGruppe {
  n: number;
  share: number | null;
  mature: number;
  paid: number;
  pay_rate: number | null;
  expected_value: number | null;
}
export interface AnmeldungAntwortP2 extends Omit<AnmeldungAntwort, 'quartale' | 'produkte' | 'kanaele' | 'kennzahlen'> {
  kennzahlen: AnmeldungAntwort['kennzahlen'] & { veraenderung_leads?: number | null };
  leads_bis?: string;
  fenster_min_anteil?: number;
  /** Reife der Bezahlquote „Erste Anfo bezahlt" (`MATURE_DAYS`). */
  reife_tage?: number;
  quartale?: AnmeldungQuartalP2[];
  produkte: ({ plx_product: string; name: string | null; einmalkauf?: boolean } & ErsteAnfoGruppe)[];
  kanaele?: ({ channel: string; label: string } & ErsteAnfoGruppe)[];
}

export const fetchKennzahlenP2 = () => unwrap<KennzahlenAntwortP2>(`${UM}/kennzahlen`);
export const fetchBezahlqualitaetP2 = () => unwrap<QualitaetAntwortP2>(`${UM}/bezahlqualitaet`);
export const fetchKohortenP2 = () => unwrap<KohortenAntwortP2>(`${UM}/kohorten`);
export const fetchKundentypP2 = () => unwrap<KundentypAntwortP2>(`${UM}/kundentyp`);
export const fetchZahlungseingang = () => unwrap<ZahlungseingangAntwort>(`${UM}/zahlungseingang`);
export const fetchAnmeldungBestellungP2 = () => unwrap<AnmeldungAntwortP2>(`${UM}/anmeldung-bestellung`);
/** K4a und K4b lesen dieselbe Antwort: eine Anfrage je Seitenaufruf (Muster `fetchWebinarRankingGeteilt`, 60 s). */
let prognoseGeteilt: { at: number; p: Promise<PrognoseAntwortP2> } | null = null;
export const fetchPrognoseGeteilt = (): Promise<PrognoseAntwortP2> => {
  if (!prognoseGeteilt || Date.now() - prognoseGeteilt.at > 60_000) {
    const p = unwrap<PrognoseAntwortP2>(`${UM}/prognose`);
    prognoseGeteilt = { at: Date.now(), p };
    p.catch(() => {
      prognoseGeteilt = null;
    });
  }
  return prognoseGeteilt.p;
};
